import React, { useState, useCallback, useEffect } from 'react';
import OutputReportSearchFilters from './OutputReportSearchFilters';
import OutputReportTable from './OutputReportTable';
import OutputReportManualInsert from './OutputReportManualInsert';
import {
    searchOutputReports,
    insertOutputReportTransfer,
    softDeleteOutputReport,
    restoreOutputReport,
    markOutputReportDownloaded,
    extendOutputReportExpiry,
} from './Transactions';

const OutputReportAdmin = ({ api, initialCriteria, lockedRecipientPartyId }) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastCriteria, setLastCriteria] = useState(null);

    const handleSearch = useCallback(async (criteria) => {
        setLoading(true);
        setError(null);
        // When locked to a party (per-user view), force the recipient onto every
        // search so the filter form can't drop or override it (review #7).
        const effectiveCriteria = lockedRecipientPartyId
            ? { ...criteria, RecipientPartyId: lockedRecipientPartyId }
            : criteria;
        setLastCriteria(effectiveCriteria);
        const { data, error: err } = await searchOutputReports(api, effectiveCriteria);
        if (err) {
            setError(err.message || 'Search failed');
            setReports([]);
        } else {
            setReports(data || []);
        }
        setLoading(false);
    }, [api, lockedRecipientPartyId]);

    const refreshSearch = useCallback(async () => {
        if (lastCriteria) {
            await handleSearch(lastCriteria);
        }
    }, [lastCriteria, handleSearch]);

    useEffect(() => {
        if (lockedRecipientPartyId) {
            handleSearch({});
        } else if (initialCriteria) {
            handleSearch(initialCriteria);
        }
    }, []);

    const handleInsert = useCallback(async (model) => {
        setLoading(true);
        setError(null);
        const { error: err } = await insertOutputReportTransfer(api, model);
        if (err) {
            setError(err.message || 'Insert failed');
        } else {
            await refreshSearch();
        }
        setLoading(false);
    }, [api, refreshSearch]);

    const handleAction = useCallback(async (action, id, extra) => {
        setError(null);
        let result;
        switch (action) {
            case 'markUnread':
                result = await markOutputReportDownloaded(api, id, false);
                break;
            case 'delete':
                result = await softDeleteOutputReport(api, id);
                break;
            case 'restore':
                result = await restoreOutputReport(api, id);
                break;
            case 'extendExpiry':
                result = await extendOutputReportExpiry(api, id, extra);
                break;
            default:
                return;
        }
        if (result.error) {
            setError(result.error.message || `${action} failed`);
        } else {
            await refreshSearch();
        }
    }, [api, refreshSearch]);

    return (
        <div style={{ padding: '20px' }}>
            <h2>Output Report Admin</h2>

            {error && (
                <div style={{ color: 'red', padding: '10px', marginBottom: '10px', border: '1px solid red' }}>
                    {error}
                </div>
            )}

            <OutputReportManualInsert
                onInsert={handleInsert}
                loading={loading}
                lockedRecipientPartyId={lockedRecipientPartyId}
            />

            <OutputReportSearchFilters
                onSearch={handleSearch}
                loading={loading}
                lockedRecipientPartyId={lockedRecipientPartyId}
            />

            <OutputReportTable
                reports={reports}
                api={api}
                onAction={handleAction}
            />
        </div>
    );
};

export default OutputReportAdmin;
